import { useCallback, useEffect } from "react"

import { getAddress } from "@/app/actions/geocode"

import { Address, Coordinates, useLocationStore } from "./store"

export type GeolocationErrorType =
  | "PERMISSION_DENIED"
  | "POSITION_UNAVAILABLE"
  | "TIMEOUT"
  | "UNSUPPORTED"
  | "UNKNOWN"

export interface GeolocationError {
  type: GeolocationErrorType
  message: string
}

export function useLocation() {
  const {
    coordinates,
    address,
    isLoading,
    error,
    setCoordinates,
    setAddress,
    setLoading,
    setError,
    reset,
  } = useLocationStore()

  const getCurrentPosition = useCallback(async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              let type: GeolocationErrorType = "UNKNOWN"

              switch (error.code) {
                case error.PERMISSION_DENIED:
                  type = "PERMISSION_DENIED"
                  break
                case error.POSITION_UNAVAILABLE:
                  type = "POSITION_UNAVAILABLE"
                  break
                case error.TIMEOUT:
                  type = "TIMEOUT"
                  break
                default:
                  type = "UNKNOWN"
              }

              reject({
                type,
                message: error.message || "Unable to retrieve your location",
              })
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 600000, // 10 minutes
            }
          )
        }
      )

      const newCoordinates: Coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }

      setCoordinates(newCoordinates)

      // Fetch address information using the geocode API
      try {
        const geocodeResponse = await getAddress(
          newCoordinates.latitude,
          newCoordinates.longitude,
          true
        )

        if (geocodeResponse?.address_full?.results?.[0]) {
          const result = geocodeResponse.address_full.results[0]
          const newAddress: Address = {
            full: result.formatted_address,
            display: result.display_name || result.formatted_address,
            placeId: result.place_id,
          }
          setAddress(newAddress)
        } else {
          // If the API doesn't return the expected format, use a fallback
          setAddress({
            full: geocodeResponse?.address || "Unknown location",
            display: geocodeResponse?.address || "Unknown location",
          })
        }
      } catch (geocodeError) {
        console.error("Error fetching address:", geocodeError)
        // Don't set the error state because we at least got coordinates
        setAddress({
          full: "Location found, but address lookup failed",
          display: "Unknown address",
        })
      }

      setLoading(false)
    } catch (error) {
      const geoError = error as GeolocationError
      setError(geoError.message)
      setCoordinates(null)
      setAddress(null)
    }
  }, [setCoordinates, setAddress, setLoading, setError])

  // Function to set user address from autocomplete
  const setUserAddress = useCallback(
    (newAddress: Address) => {
      setAddress(newAddress)
    },
    [setAddress]
  )

  // Add the hasLocation getter that was in the context
  const hasLocation = coordinates !== null

  return {
    coordinates,
    address,
    isLoading,
    error,
    hasLocation,
    getCurrentPosition,
    setUserAddress,
    reset,
  }
}

export function useCoordinatesParam() {
  const { coordinates } = useLocationStore()

  // Return formatted coordinates for API requests
  return coordinates
    ? {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      }
    : null
}

// Optional hook to auto-fetch location on component mount
export function useAutoLocation(autoFetch = false) {
  const { getCurrentPosition, coordinates } = useLocation()

  useEffect(() => {
    if (autoFetch && !coordinates) {
      getCurrentPosition()
    }
  }, [autoFetch, coordinates, getCurrentPosition])
}
